<template>
  <div class="order-total-summary">
    <!-- Dual Pricing Display (Expert UI/UX for Cash vs Card) -->
    <div
      v-if="displayMode === 'dual'"
      class="summary-card dual-pricing-wrapper"
    >
      <!-- Title -->
      <div class="dual-pricing-title">
        Total Amount Due
      </div>

      <!-- Subtotal / Tax breakdown when available -->
      <div
        v-if="showSubtotalTaxBreakdown"
        class="breakdown-before-dual"
      >
        <div class="summary-row">
          <span class="label">Subtotal</span>
          <span class="value">{{ currency }}{{ displaySubtotal.toFixed(2) }}</span>
        </div>
        <div class="summary-row">
          <span class="label">Tax</span>
          <span class="value">{{ currency }}{{ displayTaxTotal.toFixed(2) }}</span>
        </div>
        <div class="summary-divider" />
      </div>

      <!-- Price Cards -->
      <div class="dual-pricing-container">
        <div class="dual-price-card cash-price">
          <div class="price-header">
            <i class="fa fa-money-bill-wave" />
            PAY WITH CASH
          </div>
          <div class="price-value">
            <span class="currency">{{ currency }}</span>
            <span class="val">{{ total.toFixed(2) }}</span>
          </div>
        </div>

        <div class="dual-price-card card-price">
          <div class="price-header">
            <i class="fa fa-credit-card" />
            PAY WITH CARD
          </div>
          <div class="price-value">
            <span class="currency">{{ currency }}</span>
            <span class="val">{{ finalTotal.toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <!-- Order Meta Info (Scheduling & Customer) - Below dual pricing -->
      <div
        v-if="schedulingText || customerName"
        class="order-meta-info"
      >
        <div
          v-if="schedulingText"
          class="meta-item"
        >
          <div class="meta-item-left">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="meta-icon"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span class="meta-label">Pickup Time:</span>
          </div>
          <span class="meta-text">{{ schedulingText }}</span>
        </div>
        <div
          v-if="customerName"
          class="meta-item"
        >
          <div class="meta-item-left">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="meta-icon"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle
                cx="12"
                cy="7"
                r="4"
              />
            </svg>
            <span class="meta-label">Customer:</span>
          </div>
          <span class="meta-text">{{ customerName }}</span>
        </div>
      </div>
    </div>

    <!-- Breakdown Display (Single Surcharge Method) -->
    <div
      v-else-if="displayMode === 'breakdown'"
      class="summary-card breakdown-card"
    >
      <div
        v-if="showSubtotalTaxBreakdown"
        class="breakdown-rows"
      >
        <div class="summary-row">
          <span class="label">Subtotal</span>
          <span class="value">{{ currency }}{{ displaySubtotal.toFixed(2) }}</span>
        </div>
        <div class="summary-row">
          <span class="label">Tax</span>
          <span class="value">{{ currency }}{{ displayTaxTotal.toFixed(2) }}</span>
        </div>
      </div>
      <div
        v-else
        class="summary-row"
      >
        <span class="label">Order</span>
        <span class="value">{{ currency }}{{ total.toFixed(2) }}</span>
      </div>
      <div class="summary-row">
        <span class="label">Non-Cash Fee</span>
        <span
          class="value"
        >{{ currency }}{{ surchargeAmount.toFixed(2) }}</span>
      </div>
      <div class="summary-divider" />
      <div class="summary-row total-row">
        <span class="label">TOTAL AMOUNT DUE</span>
        <span class="value">{{ currency }}{{ finalTotal.toFixed(2) }}</span>
      </div>

      <!-- Order Meta Info (Scheduling & Customer) -->
      <div
        v-if="schedulingText || customerName"
        class="order-meta-info"
      >
        <div
          v-if="schedulingText"
          class="meta-item"
        >
          <div class="meta-item-left">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="meta-icon"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span class="meta-label">Pickup Time:</span>
          </div>
          <span class="meta-text">{{ schedulingText }}</span>
        </div>
        <div
          v-if="customerName"
          class="meta-item"
        >
          <div class="meta-item-left">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="meta-icon"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle
                cx="12"
                cy="7"
                r="4"
              />
            </svg>
            <span class="meta-label">Customer:</span>
          </div>
          <span class="meta-text">{{ customerName }}</span>
        </div>
      </div>
    </div>

    <!-- Standard Total Display (Default - Always shown) -->
    <div
      v-else
      class="summary-card total-card"
    >
      <template v-if="showSubtotalTaxBreakdown">
        <div class="summary-row">
          <span class="label">Subtotal</span>
          <span class="value">{{ currency }}{{ displaySubtotal.toFixed(2) }}</span>
        </div>
        <div class="summary-row">
          <span class="label">Tax</span>
          <span class="value">{{ currency }}{{ displayTaxTotal.toFixed(2) }}</span>
        </div>
        <div class="summary-divider" />
        <div class="total-label">
          Total
        </div>
        <div class="total-amount">
          <span class="currency">{{ currency }}</span>
          <span class="val">{{ total.toFixed(2) }}</span>
        </div>
      </template>
      <template v-else>
        <div class="total-label">
          Total Amount Due
        </div>
        <div class="total-amount">
          <span class="currency">{{ currency }}</span>
          <span class="val">{{ total.toFixed(2) }}</span>
        </div>
      </template>

      <!-- Order Meta Info (Scheduling & Customer) -->
      <div
        v-if="schedulingText || customerName"
        class="order-meta-info"
      >
        <div
          v-if="schedulingText"
          class="meta-item"
        >
          <div class="meta-item-left">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="meta-icon"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span class="meta-label">Pickup Time:</span>
          </div>
          <span class="meta-text">{{ schedulingText }}</span>
        </div>
        <div
          v-if="customerName"
          class="meta-item"
        >
          <div class="meta-item-left">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="meta-icon"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle
                cx="12"
                cy="7"
                r="4"
              />
            </svg>
            <span class="meta-label">Customer:</span>
          </div>
          <span class="meta-text">{{ customerName }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "OrderTotalSummary",
  props: {
    total: {
      type: Number,
      required: true,
    },
    currency: {
      type: String,
      default: "$",
    },
    subtotal: {
      type: Number,
      default: null,
    },
    taxTotal: {
      type: Number,
      default: null,
    },
    surchargeValue: {
      type: Number,
      default: 0,
    },
    paymentTypes: {
      type: Array,
      default: () => [],
    },
    schedulingText: {
      type: String,
      default: "",
    },
    customerName: {
      type: String,
      default: "",
    },
  },
  computed: {
    showSubtotalTaxBreakdown() {
      return (
        typeof this.subtotal === "number" && typeof this.taxTotal === "number"
      );
    },
    displaySubtotal() {
      return this.subtotal != null ? this.subtotal : 0;
    },
    displayTaxTotal() {
      return this.taxTotal != null ? this.taxTotal : 0;
    },
    hasDualPricing() {
      if (this.paymentTypes.length < 2) return false;

      const hasCash = this.paymentTypes.some(
        (p) => p.behavior === 1 || p.name.toLowerCase().includes("cash"),
      );
      const hasSurcharge = this.paymentTypes.some((p) => p.surcharge > 0);

      return hasCash && hasSurcharge;
    },
    hasSurcharge() {
      return this.surchargeValue > 0 || this.surchargeAmount > 0;
    },
    displayMode() {
      // Determine which display mode to use
      let mode = "standard";
      if (this.hasDualPricing) {
        mode = "dual";
      } else if (this.hasSurcharge) {
        mode = "breakdown";
      }

      return mode;
    },
    surchargeAmount() {
      // Use the surcharge from the first surcharge-based method found if not passed directly
      const effectiveSurchargeValue =
        this.surchargeValue ||
        this.paymentTypes.find((p) => p.surcharge > 0)?.surcharge ||
        0;

      if (!effectiveSurchargeValue) return 0;
      return parseFloat(
        (this.total * (effectiveSurchargeValue / 100)).toFixed(2),
      );
    },
    finalTotal() {
      return parseFloat((this.total + this.surchargeAmount).toFixed(2));
    },
  },
};
</script>

<style scoped>
/* Dual Pricing Styles */
.dual-pricing-wrapper {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.dual-pricing-title {
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0;
  font-weight: 700;
  text-align: center;
}

.dual-pricing-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 0;
}

.dual-price-card {
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem 1rem;
  text-align: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  transition: all 0.2s ease;
}

.dual-price-card:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.cash-price {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.cash-price .price-header {
  color: rgba(255, 255, 255, 0.8);
}

.cash-price .price-value {
  color: white;
}

.card-price {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.card-price .price-header {
  color: rgba(255, 255, 255, 0.8);
}

.card-price .price-value {
  color: white;
}

.price-header {
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
}

.price-value {
  font-weight: 700;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.price-value .currency {
  font-size: 1.75rem;
  margin-top: 0.5rem;
  margin-right: 4px;
}

.price-value .val {
  font-size: 4rem;
  line-height: 1;
}

.price-footer {
  font-size: 0.85rem;
  font-weight: 600;
  color: #718096;
  margin-top: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Existing styles */
.order-total-summary {
  margin-bottom: 1.5rem;
}

.summary-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
}

.total-card {
  text-align: center;
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
}

.total-card .summary-row {
  justify-content: space-between;
  text-align: left;
}

.breakdown-before-dual .summary-row,
.breakdown-before-dual .summary-divider {
  margin-bottom: 0.5rem;
}

.breakdown-before-dual .summary-divider {
  margin-top: 0.75rem;
  margin-bottom: 1rem;
}

.total-label {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.75rem;
  font-weight: 700;
}

.total-amount {
  color: white;
  font-weight: 700;
}

.total-amount .currency {
  font-size: 1.5rem;
  margin-top: 0.5rem;
  margin-right: 4px;
  font-family: "Merriweather", serif;
}

.total-amount .val {
  font-size: 3.75rem;
  line-height: 1;
  font-family: "Merriweather", serif;
}

.currency {
  font-size: 1.5rem;
  vertical-align: top;
  margin-right: 4px;
  margin-top: 0.5rem;
  display: inline-block;
  font-family: "Merriweather", serif;
}

.val {
  font-size: 3.75rem;
  line-height: 1;
  font-family: "Merriweather", serif;
}

/* Breakdown styles */
.breakdown-card {
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.summary-row .label {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
}

.summary-row .value {
  color: white;
  font-weight: 700;
}

.summary-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 1.5rem 0;
}

.total-row {
  margin-bottom: 0;
  flex-direction: column;
}

.total-row .label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.5rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.total-row .value {
  color: white;
  font-size: 2.5rem;
  font-weight: 900;
  font-family: "Merriweather", serif;
}

/* Order Meta Info Styles */
.order-meta-info {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.meta-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.125rem;
}

.meta-item-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.meta-icon {
  width: 18px;
  height: 18px;
  color: rgba(245, 158, 11, 0.9);
  flex-shrink: 0;
}

.meta-label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.125rem;
}

.meta-text {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.125rem;
  text-align: right;
}
</style>
